import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from '@kmgestao/ui';

import { useCreateProspectForm } from '../../../features/onboarding/index.js';
import { copyToClipboard } from '../../../lib/clipboard.js';

import { GenerateProspectLinkForm } from './GenerateProspectLinkForm.js';
import { GenerateProspectLinkResult } from './GenerateProspectLinkResult.js';

export interface GenerateProspectLinkDialogProps {
  /** Trigger custom. Default: botão "Gerar link de auto-cadastro". */
  trigger?: ReactNode;
}

/**
 * Dialog para gerar um **link de auto-cadastro** que Karla envia
 * pelo canal que preferir (WhatsApp, email avulso, LinkedIn, SMS).
 *
 * **Fluxo**:
 *   1. Karla clica "Gerar link de auto-cadastro" na página de leads
 *   2. Opcionalmente informa email/WhatsApp (só para rastrear depois)
 *   3. Clica "Gerar" — edge function cria a submission pending
 *   4. Dialog muda para tela "link gerado": URL copiável + botão
 *      "Copiar" + orientação de como usar
 *   5. Karla copia e cola onde quiser
 *
 * **Por que 2 estados** (formulário → link gerado) em vez de fechar
 * o dialog: Karla precisa VER o link copiado com feedback visual
 * antes de sair. Fechar e mostrar o link via toast seria confuso em
 * mobile (toast some rápido).
 */
export function GenerateProspectLinkDialog({ trigger }: GenerateProspectLinkDialogProps) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useCreateProspectForm();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Reset ao fechar — próxima abertura começa limpa.
      setEmail('');
      setPhone('');
      setGeneratedUrl(null);
      setCopied(false);
    }
  };

  const handleGenerate = async () => {
    try {
      const result = await mutation.mutateAsync({
        prospect_email: email.trim() || null,
        prospect_phone: phone.trim() || null,
      });
      setGeneratedUrl(result.public_url);
    } catch (err) {
      toast.error('Não foi possível gerar o link', {
        description: (err as Error).message,
      });
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    const ok = await copyToClipboard(generatedUrl);
    if (ok) {
      setCopied(true);
      toast.success('Link copiado! Cole no WhatsApp/email para enviar.');
      // Reseta visual após 2s para permitir copiar de novo.
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="lg">
            Gerar link de auto-cadastro
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{generatedUrl ? 'Link gerado' : 'Gerar link de auto-cadastro'}</DialogTitle>
          <DialogDescription>
            {generatedUrl
              ? 'Copie o link abaixo e envie pelo WhatsApp, email ou outro canal da sua preferência. O lead preenche identificação e questionário sozinho, e aparece no funil assim que submeter.'
              : 'Gere um link único para um novo lead preencher identificação e questionário sozinho. Útil quando você tem só o email ou WhatsApp dele. Não enviamos email — você compartilha pelo canal que preferir.'}
          </DialogDescription>
        </DialogHeader>

        {generatedUrl ? (
          <GenerateProspectLinkResult url={generatedUrl} copied={copied} onCopy={handleCopy} />
        ) : (
          <GenerateProspectLinkForm
            email={email}
            phone={phone}
            setEmail={setEmail}
            setPhone={setPhone}
          />
        )}

        <DialogFooter className="mt-4 gap-2">
          {generatedUrl ? (
            <DialogClose asChild>
              <Button size="lg" className="w-full sm:w-auto">
                Fechar
              </Button>
            </DialogClose>
          ) : (
            <>
              <DialogClose asChild>
                <Button type="button" variant="outline" size="lg">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="button"
                size="lg"
                onClick={handleGenerate}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Gerando…' : 'Gerar link'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
